import { MAX_SAFARI_CANVAS_SIZE } from '@constants/global';

interface PixelCrop {
  width: number;
  height: number;
  x: number;
  y: number;
}

export interface CroppedImageResult {
  file: File;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const limitSize = (width: number, height: number, maximumPixels: number) => {
  const requiredPixels = width * height;
  if (requiredPixels <= maximumPixels) return { width, height };

  const scalar = Math.sqrt(maximumPixels / requiredPixels);
  return {
    width: Math.floor(width * scalar),
    height: Math.floor(height * scalar),
  };
};

export const getCroppedImg = async (
  imageSrc: string,
  imgType: string,
  pixelCrop: PixelCrop,
  rotation: number = 0
): Promise<CroppedImageResult | undefined> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;

  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) return undefined;

  const radians = toRadians(rotation);
  const rotatedSize = {
    width: Math.abs(image.width * Math.cos(radians)) + Math.abs(image.height * Math.sin(radians)),
    height: Math.abs(image.width * Math.sin(radians)) + Math.abs(image.height * Math.cos(radians)),
  };

  const limitedSize = limitSize(rotatedSize.width, rotatedSize.height, MAX_SAFARI_CANVAS_SIZE);
  const scale = Math.min(limitedSize.width / rotatedSize.width, limitedSize.height / rotatedSize.height);

  tempCanvas.width = limitedSize.width;
  tempCanvas.height = limitedSize.height;

  tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
  tempCtx.rotate(radians);
  tempCtx.translate(-tempCanvas.width / 2, -tempCanvas.height / 2);

  const scaledWidth = image.width * scale;
  const scaledHeight = image.height * scale;
  const drawX = (tempCanvas.width - scaledWidth) / 2;
  const drawY = (tempCanvas.height - scaledHeight) / 2;
  tempCtx.drawImage(image, drawX, drawY, scaledWidth, scaledHeight);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const cropX = (tempCanvas.width - scaledWidth) / 2 + pixelCrop.x * scale;
  const cropY = (tempCanvas.height - scaledHeight) / 2 + pixelCrop.y * scale;
  ctx.drawImage(
    tempCanvas,
    cropX,
    cropY,
    pixelCrop.width * scale,
    pixelCrop.height * scale,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas toBlob returned null'));
        return;
      }
      const file = new File([blob], 'cropped-image', { lastModified: Date.now(), type: imgType });
      resolve({ file });
    }, imgType);
  });
};
